<template>
  <div class="table-container">
    <!-- 固定顶部区域 -->
    <div class="fixed-header">
      <div>
        <el-button type="primary" :disabled="loading" @click="openDialog(null)">新建 API Key</el-button>
      </div>
    </div>
    
    <!-- 表格区域 -->
    <div class="table-wrapper">
      <el-table v-loading="loading" :data="data" height="calc(100vh - 200px)">
        <el-table-column label="ID" width="70" prop="id"></el-table-column>
        <el-table-column label="名称" width="200" prop="name"></el-table-column>
        <el-table-column label="API Key 前缀" width="200" prop="keyPrefix">
          <template #default="scope">
            <code>{{ scope.row.keyPrefix }}...</code>
          </template>
        </el-table-column>
        <el-table-column label="允许的域名" width="300">
          <template #default="scope">
            <el-tag
              v-for="domain in scope.row.allowedDomains"
              :key="domain"
              style="margin: 2px"
              size="small"
            >
              {{ domain }}
            </el-tag>
            <span v-if="!scope.row.allowedDomains || scope.row.allowedDomains.length === 0" style="color: #999;">
              无限制
            </span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.isActive ? 'success' : 'danger'">
              {{ scope.row.isActive ? '激活' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="150" prop="createdAt"></el-table-column>
        <el-table-column label="最后使用" width="150" prop="lastUsedAt">
          <template #default="scope">
            {{ scope.row.lastUsedAt || '从未使用' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button size="small" :disabled="loading" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" :disabled="loading" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 固定分页区域 -->
    <div class="fixed-pagination">
      <div style="display: flex; justify-content: space-between">
        <el-pagination
          layout="prev, pager, next"
          :total="total"
          :page-size="query.limit || 20"
          @current-change="pageChange"
          :disabled="loading"
        ></el-pagination>
        <el-select v-model="query.limit" style="width: 100px" @change="reload" :disabled="loading">
          <el-option :value="20"></el-option>
          <el-option :value="50"></el-option>
          <el-option :value="100"></el-option>
        </el-select>
      </div>
    </div>
    
    <!-- 编辑对话框 -->
    <el-dialog v-model="dialog_opened" :title="editing ? '编辑 API Key' : '新建 API Key'" :before-close="reset">
      <el-form :model="form" label-width="120px">
        <el-form-item label="名称" required>
          <el-input v-model="form.name" placeholder="输入 API Key 名称"></el-input>
        </el-form-item>
        <el-form-item label="允许的域名">
          <el-input
            v-model="domainInput"
            placeholder="输入域名，多个域名用逗号分隔"
            @blur="updateDomains"
          ></el-input>
          <div style="margin-top: 8px; font-size: 12px; color: #666;">
            留空表示不限制域名。例如: example.com, api.example.com
          </div>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="form.isActive"
            :active-value="true"
            :inactive-value="false"
          ></el-switch>
        </el-form-item>
        <el-form-item v-if="!editing && form.key" label="API Key">
          <el-input v-model="form.key" readonly>
            <template #append>
              <el-button @click="copyKey">复制</el-button>
            </template>
          </el-input>
          <div style="margin-top: 8px; font-size: 12px; color: #f56c6c;">
            请妥善保管此 API Key，创建后将无法再次查看完整 Key！
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="loading" @click="reset" text>取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ApiKeyApi, ApiKey } from '../../api/apikeyApi';

export default defineComponent({
  name: 'ApiKey',
  data() {
    return {
      loading: false,
      data: [] as ApiKey[],
      editing: null as ApiKey | null,
      dialog_opened: false,
      total: 0,
      page: 1,
      query: {
        limit: 20,
        skip: 0,
      },
      form: {
        name: '',
        allowedDomains: [] as string[],
        isActive: true,
        key: '' as string | undefined,
      },
      domainInput: '',
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        const result = await ApiKeyApi.getApiKeys(this.query.skip, this.query.limit);
        this.data = result.apiKeys;
        this.total = result.total;
      } catch (error) {
        ElMessage.error('加载失败');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    pageChange(page: number): void {
      this.query.skip = (page - 1) * this.query.limit;
      this.init();
    },
    reload(): void {
      this.query.skip = 0;
      this.page = 1;
      this.init();
    },
    openDialog(row: ApiKey | null): void {
      this.editing = row;
      if (row) {
        this.form = {
          name: row.name,
          allowedDomains: [...(row.allowedDomains || [])],
          isActive: row.isActive,
          key: undefined,
        };
        this.domainInput = row.allowedDomains?.join(', ') || '';
      } else {
        this.form = {
          name: '',
          allowedDomains: [],
          isActive: true,
          key: undefined,
        };
        this.domainInput = '';
      }
      this.dialog_opened = true;
    },
    updateDomains(): void {
      if (this.domainInput.trim()) {
        this.form.allowedDomains = this.domainInput
          .split(',')
          .map(d => d.trim())
          .filter(d => d.length > 0);
      } else {
        this.form.allowedDomains = [];
      }
    },
    async submit(): Promise<void> {
      if (!this.form.name.trim()) {
        ElMessage.warning('请输入名称');
        return;
      }

      this.updateDomains();
      this.loading = true;

      try {
        if (this.editing) {
          // 更新
          await ApiKeyApi.updateApiKey(
            this.editing.id,
            this.form.name,
            this.form.allowedDomains,
            this.form.isActive
          );
          ElMessage.success('更新成功');
        } else {
          // 创建
          const result = await ApiKeyApi.createApiKey(
            this.form.name,
            this.form.allowedDomains
          );
          this.form.key = result.key; // 保存完整 key 用于显示
          ElMessage.success('创建成功，请保存 API Key');
          // 不关闭对话框，让用户复制 key
          await this.init();
          return;
        }
        this.reset();
        await this.init();
      } catch (error) {
        ElMessage.error('操作失败');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    reset(): void {
      this.dialog_opened = false;
      this.editing = null;
      this.form = {
        name: '',
        allowedDomains: [],
        isActive: true,
        key: undefined,
      };
      this.domainInput = '';
    },
    async remove(row: ApiKey): Promise<void> {
      try {
        await ElMessageBox.confirm('确定要删除此 API Key 吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });

        this.loading = true;
        try {
          await ApiKeyApi.deleteApiKey(row.id);
          ElMessage.success('删除成功');
          await this.init();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        } finally {
          this.loading = false;
        }
      } catch {
        // 用户取消
      }
    },
    copyKey(): void {
      if (this.form.key) {
        navigator.clipboard.writeText(this.form.key).then(() => {
          ElMessage.success('已复制到剪贴板');
        }).catch(() => {
          ElMessage.error('复制失败');
        });
      }
    },
  },
});
</script>

<style scoped>
.table-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 16px;
}

.fixed-header {
  background-color: #fff;
  padding: 16px;
  border-bottom: 1px solid #ebeef5;
  z-index: 10;
  flex-shrink: 0;
}

.table-wrapper {
  flex: 1;
  overflow: hidden;
  background-color: #fff;
}

.table-wrapper .el-table {
  width: 100%;
}

.table-wrapper :deep(.el-table__header-wrapper) {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
}

.fixed-pagination {
  background-color: #fff;
  padding: 10px 0;
  border-top: 1px solid #ebeef5;
  z-index: 10;
  flex-shrink: 0;
}
</style>
